import React, {useId, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils/logUtils'
import {ACCESS_LABELS} from './constants'
import {shareErrorMessage} from './errors'
import {useDebouncedValue, useEligibleMembers, useInviteToShare} from './managementHooks'
import type {EligibleMember, MemberAccess} from './types'

const EMAIL_RE = /^[^@\s"\\<>,;:()[\]]+@[^@\s]+\.[^@\s]+$/

interface ShareInviteFormProps {
  shareId: string
  /** Subs already in the folder; they are left out of the suggestions. */
  memberSubs: Set<string>
}

/**
 * Invite someone to a shared folder: pick a member of your subscriptions (Q3, `eligible-members`,
 * which lists the members of every subscription you are in) or type any email address, and choose
 * Can view or Can edit. Whether an address outside your subscriptions is allowed is their setting
 * (D3); the server says so if not.
 */
export const ShareInviteForm: React.FC<ShareInviteFormProps> = ({shareId, memberSubs}) => {
  const [text, setText] = useState('')
  const [picked, setPicked] = useState<EligibleMember | null>(null)
  const [access, setAccess] = useState<MemberAccess>('view')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const listboxId = useId()
  const inputId = useId()

  const query = useDebouncedValue(text.trim(), 250)
  const eligible = useEligibleMembers(query, open && !picked)
  const invite = useInviteToShare(shareId)

  const suggestions = useMemo(
    () => (eligible.data?.members ?? []).filter((m) => !memberSubs.has(m.sub)).slice(0, 8),
    [eligible.data, memberSubs]
  )
  const showList = open && !picked && suggestions.length > 0

  const trimmed = text.trim()
  const canSubmit = !invite.isLoading && (!!picked || EMAIL_RE.test(trimmed))

  const choose = (member: EligibleMember) => {
    setPicked(member)
    setText(member.name ? `${member.name} <${member.email}>` : member.email)
    setOpen(false)
    setError(null)
  }

  const submit = () => {
    if (!canSubmit) {
      if (trimmed) setError('Choose a member from the list or type a full email address.')
      return
    }
    const target = picked ? {memberSub: picked.sub} : {email: trimmed.toLowerCase()}
    const who = picked ? picked.name || picked.email : trimmed
    setError(null)
    invite.mutate(
      {target, access},
      {
        onSuccess: () => {
          toast.success(`Invitation sent to ${who}`, toastDefaultConfig)
          setText('')
          setPicked(null)
        },
        onError: (err) => {
          setError(shareErrorMessage(err))
          getLogger().warn('Share invitation failed', err)
        },
      }
    )
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault()
      setOpen(true)
      setHighlight((h) => (h + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault()
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (showList && suggestions[highlight]) {
        choose(suggestions[highlight])
      } else {
        submit()
      }
    } else if (e.key === 'Escape' && showList) {
      // Close the list, not the dialog around it.
      e.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <div className='ob-sharing-invite' data-testid='sharing-invite-form'>
      <label htmlFor={inputId} className='form-label fw-semibold mb-1'>
        Invite people
      </label>
      <div className='d-flex gap-2 flex-wrap flex-sm-nowrap'>
        <div className='position-relative flex-grow-1'>
          <input
            id={inputId}
            type='text'
            className='form-control form-control-sm'
            placeholder='Name or email address'
            autoComplete='off'
            role='combobox'
            aria-expanded={showList}
            aria-controls={listboxId}
            aria-autocomplete='list'
            aria-activedescendant={showList ? `${listboxId}-${highlight}` : undefined}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setPicked(null)
              setOpen(true)
              setHighlight(0)
              setError(null)
            }}
            onFocus={() => setOpen(true)}
            // Let a click on a suggestion land before the list goes away.
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={onKeyDown}
            disabled={invite.isLoading}
          />
          {showList && (
            <ul
              id={listboxId}
              role='listbox'
              className='ob-sharing-suggestions list-unstyled mb-0'
              aria-label='Members of your subscriptions'
            >
              {suggestions.map((m, i) => (
                <li
                  key={m.sub}
                  id={`${listboxId}-${i}`}
                  role='option'
                  aria-selected={i === highlight}
                  className={i === highlight ? 'is-active' : undefined}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(m)}
                >
                  <span className='fw-semibold'>{m.name || m.email}</span>
                  {m.name && <span className='text-muted ms-2'>{m.email}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <select
          className='form-select form-select-sm w-auto'
          aria-label='Access for the person you invite'
          value={access}
          onChange={(e) => setAccess(e.target.value as MemberAccess)}
          disabled={invite.isLoading}
        >
          <option value='view'>{ACCESS_LABELS.view}</option>
          <option value='edit'>{ACCESS_LABELS.edit}</option>
        </select>
        <button
          type='button'
          className='btn btn-sm btn-primary text-nowrap'
          onClick={submit}
          disabled={!canSubmit}
        >
          {invite.isLoading ? 'Inviting…' : 'Invite'}
        </button>
      </div>
      <div className='form-text'>
        Can view: open, download and use files in jobs. Can edit: also upload, rename, move and
        delete inside the folder.
      </div>
      {error && (
        <div className='text-danger small mt-1' role='alert'>
          {error}
        </div>
      )}
    </div>
  )
}
